import { Injectable, inject } from '@angular/core';

import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '@services/toast.service';

import { DialogService } from '@sui/dialog';

import { CompanyApiService } from 'src/app/api/services/company-api.service';
import { DimensionAttachParams } from './feed.service';
import { UserApiService } from 'src/app/api/services/user-api.service';

import { EditorUserDialogComponent } from '../../shared/components/editor-user-dialog/editor-user-dialog.component';
import { ConfirmDialogComponent } from '../../shared/components/confirm-dialog/confirm-dialog.component';
import { QueryEditorDialogComponent } from '../../shared/components/query-editor-dialog/query-editor-dialog.component';
import { EditorCompanyDialogComponent } from 'src/app/admin/dialogs/editor-company-dialog/editor-company-dialog.component';
import { FeedCreateWizardDialogComponent } from '../../feed/dialogs/feed-create-wizard-dialog/feed-create-wizard-dialog.component';
import { UseCaseWizardDialogComponent } from '../../shared/components/use-case-wizard-dialog/use-case-wizard-dialog.component';
import { DashboardEditorDialogComponent } from '../../dashboard/dialogs/dashboard-editor-dialog/dashboard-editor-dialog.component';
import { WorkspaceEditorDialogComponent } from '../../workspace/dialogs/workspace-editor-dialog/workspace-editor-dialog.component';

import { User } from '../models/core.models';
import { NomObjectTypes } from '../constants';

import { selectUser, selectUserAccessLevelGlobalAdmin } from '../state';
import { AppState } from 'src/app/core/state/app.state';

import {
  catchError,
  firstValueFrom,
  forkJoin,
  of,
  switchMap,
  take,
} from 'rxjs';
import { EditorClose } from 'src/app/shared/utils/editor-close.utils';

@Injectable({
  providedIn: 'root',
})
export class ObjectCreatorService {
  private dialogService = inject(DialogService);
  private userApi = inject(UserApiService);

  protected companyApi = inject(CompanyApiService);
  protected store = inject(Store<AppState>);
  protected translateService = inject(TranslateService);
  protected toastService = inject(ToastService);

  createNewWorkspace(): EditorClose {
    return firstValueFrom(
      this.dialogService.openTabsDialog(WorkspaceEditorDialogComponent, {
        data: {},
        closeOnClick: false,
        closeOnEscape: true,
        width: '50vw',
        height: '50vh',
        minWidth: 800,
        minHeight: 800,
        maxWidth: 1020,
        maxHeight: 1020,
      }).closed,
      { defaultValue: null },
    );
  }

  createNewDashboard(): EditorClose {
    return firstValueFrom(
      this.dialogService.openTabsDialog(DashboardEditorDialogComponent, {
        data: {},
        closeOnClick: false,
        closeOnEscape: true,
        width: '50vw',
        height: '50vh',
        minWidth: 800,
        minHeight: 800,
        maxWidth: 1020,
        maxHeight: 1020,
      }).closed,
      { defaultValue: null },
    );
  }

  createNewFeed(): EditorClose {
    return firstValueFrom(
      this.dialogService.openTabsDialog(FeedCreateWizardDialogComponent, {
        data: {},
        closeOnClick: false,
        closeOnEscape: true,
        width: '50vw',
        height: '50vh',
        minWidth: 1536,
        minHeight: 864,
        maxWidth: 1920,
        maxHeight: 1080,
      }).closed,
      { defaultValue: null },
    );
  }

  async createNewUseCase(): EditorClose {
    // Restricted to GLOBAL_ADMIN and above (access_level >= 50). The header hides
    // this entry for lesser users; this defensive check also refuses direct
    // programmatic calls. ADR 0002's 'Use Case Provisioning' feature-component
    // gate still applies server-side and is enforced independently.
    const isGlobalAdmin = await firstValueFrom(
      this.store.select(selectUserAccessLevelGlobalAdmin).pipe(take(1)),
    );
    if (!isGlobalAdmin) {
      return null;
    }
    return firstValueFrom(
      this.dialogService.openTabsDialog(UseCaseWizardDialogComponent, {
        data: {},
        closeOnClick: false,
        closeOnEscape: true,
        width: '50vw',
        height: '50vh',
        minWidth: 1200,
        minHeight: 800,
        maxWidth: 1600,
        maxHeight: 1080,
      }).closed,
      { defaultValue: null },
    );
  }

  async createNewDimension(
    query?: string,
    dimensionAttachParams?: DimensionAttachParams,
  ): EditorClose {
    try {
      const currentUser = await firstValueFrom(
        this.store.select(selectUser).pipe(take(1)),
      );

      if (!currentUser?.id) return null;

      const availableFeedTypes = await firstValueFrom(
        this.userApi.getAvailableFeedTypes(currentUser.id),
      );

      const dimensionFeedType = availableFeedTypes.find(
        (feedType) => feedType.type_id === NomObjectTypes.SEARCH_DIMENSION,
      );

      // * check if Dimension Limit is reached
      if (!dimensionFeedType?.left) {
        const title = this.translateService.instant(
          'dimension-limit-reached.confirm-dialog.title',
        );
        const message = this.translateService.instant(
          'dimension-limit-reached.confirm-dialog.message',
        );

        this.dialogService.openSimpleDialog(ConfirmDialogComponent, {
          data: {
            title,
            message,
            hideCancelButton: true,
          },
          closeOnClick: false,
        });
        return null;
      } else {
        return firstValueFrom(
          this.dialogService.openTabsDialog(QueryEditorDialogComponent, {
            data: {
              objectTypeId: NomObjectTypes.SEARCH_DIMENSION,
              query,
              dimensionAttachParams,
            },
            closeOnClick: false,
            closeOnEscape: true,
            width: '80vw',
            height: '80vh',
          }).closed,
          { defaultValue: null },
        );
      }
    } catch (error) {
      this.toastService.showError(
        this.translateService.instant(
          'api-error.failed-to-check-dimension-quota',
        ),
      );
      return null;
    }
  }

  createNewCompany(): EditorClose {
    const emptyCompany = null;

    return firstValueFrom(
      forkJoin({
        company: of(emptyCompany),
        companyFeatures: this.companyApi.getCompanyFeatures(null),
      }).pipe(
        switchMap(
          ({ company, companyFeatures }) =>
            this.dialogService.openTabsDialog(EditorCompanyDialogComponent, {
              data: { company, companyFeatures },
              closeOnClick: false,
              closeOnEscape: true,
              width: '80%',
              height: '80%',
              minWidth: 1536,
              minHeight: 864,
              maxWidth: 1920,
              maxHeight: 1080,
            }).closed,
        ),
        catchError((error) => {
          console.error('Failed to initialize new company', error);
          return of(null);
        }),
      ),
      { defaultValue: null },
    );
  }

  createUser(): EditorClose {
    return firstValueFrom(
      this.store.select(selectUser).pipe(
        take(1),
        switchMap((currentUser: User | null) => {
          const companyId = currentUser?.active_company_id;
          return this.dialogService.openTabsDialog(EditorUserDialogComponent, {
            data: {
              user: null,
              companyId,
            },
            closeOnClick: false,
            closeOnEscape: true,
            width: '70vw',
            height: '70vh',
            minWidth: 800,
            minHeight: 800,
            maxWidth: 1020,
            maxHeight: 1020,
          }).closed;
        }),
      ),
      { defaultValue: null },
    );
  }
}
